import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import TopNav from "../components/TopNav";
import { 
    FaEnvelope, 
    FaPlane, 
    FaCheckCircle, 
    FaLock, 
    FaShieldAlt, 
    FaKey, 
    FaExclamationTriangle,
    FaInfoCircle,
    FaPaperPlane,
    FaSync
} from "react-icons/fa";
import "./ResetEmailPage.css";

/**
 * ResetEmailPage Component
 * 
 * A modern, aviation-themed email change interface for FDXVA users.
 * Allows users to securely change their email address through a verification process.
 * 
 * Flow:
 * 1. User enters new email address twice and verification code
 * 2. System sends verification link to the new email
 * 3. User clicks verification link to complete the change
 * 
 * Features:
 * - Dark mode support via props.darkmode
 * - Responsive card-based layout
 * - Progress modal for save operations
 * - Input validation with error messaging
 * - Accessible with sequential tab indexing
 * - CAPTCHA-style verification code
 * - Email change history table (filterable, paginated)
 * 
 * CSS classes are prefixed with 'rep-' (Reset Email Page) to avoid conflicts
 * 
 * @param {Object} props - Component props
 * @param {string} props.page - Current page identifier for navigation
 * @param {boolean} props.darkmode - Whether dark mode is enabled
 */
const ResetEmailPage = (props) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Form state
    const [newEmail, setNewEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [verificationInput, setVerificationInput] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [step, setStep] = useState(1); // 1: Form, 2: Email Sent, 3: Complete
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [progress, setProgress] = useState(0);
    const [modalMessage, setModalMessage] = useState("");
    const [modalIcon, setModalIcon] = useState("loading");
    
    // History table state
    const [emailHistory, setEmailHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);

    // Refs for tab focus
    const newEmailRef = useRef(null);
    const confirmEmailRef = useRef(null);
    const verificationRef = useRef(null);
    const submitRef = useRef(null);

    /**
     * Generate a random 4-character verification code
     * Uses CRC32-style hash of current timestamp
     */
    const generateVerificationCode = () => {
        const chars = "ABCDEFGHJKLMNPQRSTWXYZ23456789";
        let code = "";
        for (let i = 0; i < 4; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    };

    /**
     * Initialize component state on mount
     * Generate verification code and check for verification token in URL
     */
    useEffect(() => {
        // Generate initial verification code
        setVerificationCode(generateVerificationCode());
        
        // Check if user arrived via verification link
        const pxverf = searchParams.get("pxverf");
        if (pxverf) {
            handleVerificationLink(pxverf);
        }
        
        // Load email change history
        loadEmailHistory();
    }, []);

    /**
     * Apply filter to email history when filter text changes
     */
    useEffect(() => {
        if (filterText.trim() === "") {
            setFilteredHistory(emailHistory);
        } else {
            const filtered = emailHistory.filter(item => 
                item.oldEmail.toLowerCase().includes(filterText.toLowerCase()) ||
                item.newEmail.toLowerCase().includes(filterText.toLowerCase()) ||
                item.date.toLowerCase().includes(filterText.toLowerCase()) ||
                item.status.toLowerCase().includes(filterText.toLowerCase())
            );
            setFilteredHistory(filtered);
        }
        setCurrentPage(1);
    }, [filterText, emailHistory]);

    /**
     * Load email change history from server
     */
    const loadEmailHistory = async () => {
        try {
            // Mock data for now - replace with actual API call
            const mockHistory = [
                { id: 1, oldEmail: "old@example.com", newEmail: "new@example.com", date: "2025-01-15", status: "Completed" },
                { id: 2, oldEmail: "test@fdxva.org", newEmail: "pilot@fdxva.org", date: "2025-02-01", status: "Pending" },
            ];
            setEmailHistory(mockHistory);
            setFilteredHistory(mockHistory);
        } catch (e) {
            console.error("Failed to load email history:", e);
        }
    };

    /**
     * Handle verification link from email
     * @param {string} token - Verification token from URL
     */
    const handleVerificationLink = async (token) => {
        setLoading(true);
        setShowModal(true);
        setProgress(0);
        setModalMessage("Verifying your email change request...");
        setModalIcon("loading");
        
        const interval = setInterval(() => {
            setProgress(prev => Math.min(prev + 15, 90));
        }, 200);

        try {
            const response = await axios.post(
                "/api/user/verify_email",
                { token }
            );
            
            clearInterval(interval);
            setProgress(100);
            
            if (response.data?.success) {
                setModalMessage("Email address changed successfully!");
                setModalIcon("success");
                setStep(3);
                setSuccess(`Your email has been changed to: ${response.data.newEmail}`);
                setTimeout(() => setShowModal(false), 2500);
            } else {
                throw new Error(response.data?.message || "Verification failed or expired");
            }
        } catch (e) {
            clearInterval(interval);
            setProgress(100);
            setModalMessage(e.message || "Verification failed");
            setModalIcon("error");
            setError(e.message || "Verification failed or expired");
            setTimeout(() => setShowModal(false), 2500);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Validate email format
     * @param {string} email - Email address to validate
     * @returns {boolean} Whether email is valid
     */
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    /**
     * Handle form submission
     * Validates input and initiates email change process
     * @param {Event} e - Form submit event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (!validateEmail(newEmail)) {
            setError("Please enter a valid email address");
            return;
        }

        if (newEmail !== confirmEmail) {
            setError("Email addresses do not match");
            return;
        }

        if (verificationInput.toUpperCase() !== verificationCode) {
            setError("Incorrect verification code");
            setVerificationCode(generateVerificationCode());
            setVerificationInput("");
            return;
        }

        // Show progress modal
        setShowModal(true);
        setProgress(0);
        setModalMessage("Processing your request...");
        setModalIcon("loading");

        const interval = setInterval(() => {
            setProgress(prev => Math.min(prev + 10, 90));
        }, 150);

        try {
            console.log(newEmail)
            const response = await axios.post(
                "/api/user/change_email",
                { newEmail }
            );

            clearInterval(interval);
            setProgress(100);
            console.log(response.data)
            if (response.data?.success) {
                setModalMessage("Verification email sent!");
                setModalIcon("success");
                setStep(2);
                setSuccess(`A verification email has been sent to ${newEmail}. Please check your inbox to complete the email change.`);
                setTimeout(() => setShowModal(false), 2500);
            } else {
                throw new Error(response.data?.message || "Failed to send verification email");
            }
        } catch (e) {
            clearInterval(interval);
            setProgress(100);
            setModalMessage(e.message || "An error occurred");
            setModalIcon("error");
            setError(e.message || "Failed to process email change request");
            setTimeout(() => setShowModal(false), 2500);
        }
    };

    /**
     * Regenerate verification code
     */
    const handleRegenerateCode = () => {
        setVerificationCode(generateVerificationCode());
        setVerificationInput("");
    };

    /**
     * Reset form to initial state
     */
    const handleStartOver = () => {
        setStep(1);
        setNewEmail("");
        setConfirmEmail("");
        setVerificationInput("");
        setVerificationCode(generateVerificationCode());
        setError(null);
        setSuccess(null);
    };

    /**
     * Navigate to login page after successful email change
     */
    const handleGoToLogin = () => {
        navigate("/login");
    };

    // Pagination calculations
    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredHistory.slice(startIndex, endIndex);

    /**
     * Handle page change for history table
     * @param {number} page - Page number to navigate to
     */
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return(
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            
            <div className={`rep-container ${props.darkmode ? "rep-dark" : ""}`}>
                {/* Header Section */}
                <header className="rep-header">
                    <div className="rep-header-content">
                        <FaPlane className="rep-header-icon" aria-hidden="true" />
                        <h1>Change Email Address</h1>
                    </div>
                    <p className="rep-header-subtitle">
                        Securely update your FedEx Virtual Airlines email address
                    </p>
                </header>

                {/* Error Message */}
                {error && (
                    <div className="rep-error-message" role="alert" aria-live="polite">
                        <FaExclamationTriangle aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Success Message */}
                {success && (
                    <div className="rep-success-message" role="status" aria-live="polite">
                        <FaCheckCircle aria-hidden="true" />
                        <span>{success}</span>
                    </div>
                )}

                {/* Main Content Area */}
                <main className="rep-main">
                    {/* Step 1: Email Change Form */}
                    {step === 1 && (
                        <div className="rep-form-section" role="region" aria-label="Email change form">
                            <div className="rep-card">
                                <div className="rep-card-header">
                                    <FaEnvelope className="rep-card-icon" aria-hidden="true" />
                                    <h2>Enter New Email</h2>
                                </div>
                                <div className="rep-card-body">
                                    <form onSubmit={handleSubmit} className="rep-form">
                                        {/* Instructions */}
                                        <div className="rep-instructions">
                                            <FaInfoCircle className="rep-info-icon" aria-hidden="true" />
                                            <p>
                                                Enter your new email address below. We will send a verification 
                                                link to your new email. Your current email will not change until 
                                                you click the verification link.
                                            </p>
                                        </div>

                                        {/* New Email Field */}
                                        <div className="rep-form-group">
                                            <label htmlFor="rep-new-email">
                                                <FaEnvelope className="rep-input-icon" aria-hidden="true" />
                                                New Email Address
                                            </label>
                                            <input
                                                type="email"
                                                id="rep-new-email"
                                                ref={newEmailRef}
                                                value={newEmail}
                                                onChange={(e) => setNewEmail(e.target.value)}
                                                placeholder="Enter your new email address"
                                                required
                                                tabIndex={1}
                                                autoComplete="email"
                                                aria-required="true"
                                                aria-describedby="rep-email-help"
                                            />
                                            <span id="rep-email-help" className="rep-form-help">
                                                Use a valid email address you have access to
                                            </span>
                                        </div>

                                        {/* Confirm Email Field */}
                                        <div className="rep-form-group">
                                            <label htmlFor="rep-confirm-email">
                                                <FaCheckCircle className="rep-input-icon" aria-hidden="true" />
                                                Confirm New Email Address
                                            </label>
                                            <input
                                                type="email"
                                                id="rep-confirm-email"
                                                ref={confirmEmailRef}
                                                value={confirmEmail}
                                                onChange={(e) => setConfirmEmail(e.target.value)}
                                                placeholder="Enter your new email address again"
                                                required
                                                tabIndex={2}
                                                autoComplete="email"
                                                aria-required="true"
                                            />
                                        </div>

                                        {/* Verification Code Field */}
                                        <div className="rep-form-group">
                                            <label htmlFor="rep-verification">
                                                <FaShieldAlt className="rep-input-icon" aria-hidden="true" />
                                                Verification Code
                                            </label>
                                            <div className="rep-verification-container">
                                                <div className="rep-code-display" aria-label="Verification code to enter">
                                                    <FaKey className="rep-code-icon" aria-hidden="true" />
                                                    <span className="rep-code-text">{verificationCode}</span>
                                                    <button 
                                                        type="button" 
                                                        className="rep-btn-regenerate"
                                                        onClick={handleRegenerateCode}
                                                        tabIndex={3}
                                                        aria-label="Generate new verification code"
                                                    >
                                                        <FaSync aria-hidden="true" />
                                                    </button>
                                                </div>
                                                <input
                                                    type="text"
                                                    id="rep-verification"
                                                    ref={verificationRef}
                                                    value={verificationInput}
                                                    onChange={(e) => setVerificationInput(e.target.value.toUpperCase())}
                                                    placeholder={`Enter code: ${verificationCode}`}
                                                    required
                                                    maxLength={4}
                                                    tabIndex={4}
                                                    aria-required="true"
                                                    aria-describedby="rep-verification-help"
                                                />
                                            </div>
                                            <span id="rep-verification-help" className="rep-form-help">
                                                Enter the 4-character code shown above to verify you're human
                                            </span>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="rep-form-actions">
                                            <button
                                                type="submit"
                                                className="rep-btn rep-btn-primary"
                                                ref={submitRef}
                                                tabIndex={5}
                                                disabled={loading}
                                                aria-busy={loading}
                                            >
                                                <FaPaperPlane aria-hidden="true" />
                                                Send Verification Email
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Verification Email Sent */}
                    {step === 2 && (
                        <div className="rep-confirmation-section" role="region" aria-label="Verification email sent">
                            <div className="rep-card rep-card-success">
                                <div className="rep-card-header rep-header-success">
                                    <FaCheckCircle className="rep-card-icon" aria-hidden="true" />
                                    <h2>Verification Email Sent</h2>
                                </div>
                                <div className="rep-card-body">
                                    <div className="rep-confirmation-content">
                                        <div className="rep-plane-animation" aria-hidden="true">
                                            <FaPlane className="rep-flying-plane" />
                                        </div>
                                        <h3>Check Your Inbox</h3>
                                        <p>
                                            We've sent a verification link to <strong>{newEmail}</strong>.
                                        </p>
                                        <ul className="rep-instructions-list">
                                            <li>Check your email inbox for a message from FDXVA</li>
                                            <li>Click the verification link in the email</li>
                                            <li>Your email will be updated after verification</li>
                                            <li>If you don't see it, check your spam folder</li>
                                        </ul>
                                        <div className="rep-warning-box">
                                            <FaExclamationTriangle aria-hidden="true" />
                                            <span>
                                                Be sure to whitelist emails from @fdxva.org to receive future communications.
                                            </span>
                                        </div>
                                        <button
                                            onClick={handleStartOver}
                                            className="rep-btn rep-btn-secondary"
                                            tabIndex={1}
                                        >
                                            <FaSync aria-hidden="true" />
                                            Start Over
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Email Change Complete */}
                    {step === 3 && (
                        <div className="rep-complete-section" role="region" aria-label="Email change complete">
                            <div className="rep-card rep-card-complete">
                                <div className="rep-card-header rep-header-complete">
                                    <FaCheckCircle className="rep-card-icon" aria-hidden="true" />
                                    <h2>Email Changed Successfully</h2>
                                </div>
                                <div className="rep-card-body">
                                    <div className="rep-complete-content">
                                        <div className="rep-success-animation" aria-hidden="true">
                                            <div className="rep-checkmark-circle">
                                                <FaCheckCircle className="rep-checkmark" />
                                            </div>
                                        </div>
                                        <h3>All Done!</h3>
                                        <p>
                                            Your email address has been successfully updated. 
                                            Please use your new email address to log in.
                                        </p>
                                        <button
                                            onClick={handleGoToLogin}
                                            className="rep-btn rep-btn-primary"
                                            tabIndex={1}
                                        >
                                            <FaLock aria-hidden="true" />
                                            Go to Login
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Email Change History Table */}
                    {step === 1 && emailHistory.length > 0 && (
                        <div className="rep-history-section" role="region" aria-label="Email change history">
                            <div className="rep-card">
                                <div className="rep-card-header">
                                    <FaInfoCircle className="rep-card-icon" aria-hidden="true" />
                                    <h2>Email Change History</h2>
                                </div>
                                <div className="rep-card-body">
                                    {/* Table Filter */}
                                    <div className="rep-table-controls">
                                        <label htmlFor="rep-filter" className="rep-sr-only">
                                            Filter history
                                        </label>
                                        <input
                                            type="text"
                                            id="rep-filter"
                                            className="rep-filter-input"
                                            placeholder="Filter by email, date, or status..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            tabIndex={6}
                                            aria-label="Filter email change history"
                                        />
                                    </div>

                                    {/* Responsive Table */}
                                    <div className="rep-table-wrapper" role="region" aria-label="Email change history table">
                                        <table className="rep-table" aria-describedby="rep-table-desc">
                                            <caption id="rep-table-desc" className="rep-sr-only">
                                                List of previous email change requests showing old email, new email, date, and status
                                            </caption>
                                            <thead>
                                                <tr>
                                                    <th scope="col">Previous Email</th>
                                                    <th scope="col">New Email</th>
                                                    <th scope="col">Date</th>
                                                    <th scope="col">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.length > 0 ? (
                                                    currentItems.map((item) => (
                                                        <tr key={item.id}>
                                                            <td data-label="Previous Email">{item.oldEmail}</td>
                                                            <td data-label="New Email">{item.newEmail}</td>
                                                            <td data-label="Date">{item.date}</td>
                                                            <td data-label="Status">
                                                                <span className={`rep-status rep-status-${item.status.toLowerCase()}`}>
                                                                    {item.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="rep-no-results">
                                                            No matching records found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav className="rep-pagination" aria-label="Email history pagination">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="rep-page-btn"
                                                aria-label="Previous page"
                                            >
                                                ‹ Prev
                                            </button>
                                            <span className="rep-page-info" aria-current="page">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="rep-page-btn"
                                                aria-label="Next page"
                                            >
                                                Next ›
                                            </button>
                                        </nav>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </main>

                {/* Progress Modal */}
                {showModal && (
                    <div className="rep-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rep-modal-title">
                        <div className="rep-modal">
                            <div className="rep-modal-content">
                                {/* Modal Icon */}
                                <div className={`rep-modal-icon-container rep-modal-icon-${modalIcon}`}>
                                    {modalIcon === "loading" && (
                                        <div className="rep-spinner" aria-hidden="true"></div>
                                    )}
                                    {modalIcon === "success" && (
                                        <FaCheckCircle className="rep-modal-icon" aria-hidden="true" />
                                    )}
                                    {modalIcon === "error" && (
                                        <FaExclamationTriangle className="rep-modal-icon" aria-hidden="true" />
                                    )}
                                </div>
                                
                                {/* Modal Title */}
                                <h4 id="rep-modal-title">{modalMessage}</h4>
                                
                                {/* Progress Bar */}
                                <div className="rep-progress-container">
                                    <div className="rep-progress-bar">
                                        <div 
                                            className="rep-progress-fill"
                                            style={{ width: `${progress}%` }}
                                            role="progressbar"
                                            aria-valuenow={progress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                            aria-label={`Progress: ${progress}%`}
                                        ></div>
                                    </div>
                                    <span className="rep-progress-text">{progress}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default ResetEmailPage;
